import type { ComponentChildren } from 'preact'
import { useId, useState } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import { useToast } from '@/hooks/useToast'
import {
  DEFAULT_SENDING_DOMAIN_REGION,
  SENDING_DOMAIN_REGIONS,
  type SendingDomainViewDto,
  type SendingDomainWarmupSummaryDto,
} from '@/types/sendingDomains'
import { getDisplayErrorMessages, parseValidationErrors } from '@/utils/errorHandling'
import { routes } from '@/utils/routes'
import {
  COMPANY_WEBSITE_PLACE,
  READY_NEXT,
  REGION_HELP,
  SETTING_UP_NEXT,
  separateDomainSentence,
  settingUpNextLines,
  subdomainChoiceSentence,
  subdomainTradeOff,
  warmingNextParagraphs,
  type NewDomainKind,
} from '@/utils/sendingDomainStatus'
import { DomainOverlay } from './DomainOverlay'
import { CopyRuns } from './DomainPageParts'
import { DomainStatePill } from './DomainStatePill'
import { GHOST_BUTTON, PRIMARY_BUTTON, TEXT_LINK } from './styles'

interface AddDomainFormProps {
  clientCompanyId: string
  /** The company website's domain (CanonicalDomain); null when the company has no website set. */
  companyDomain: string | null
  summary: SendingDomainWarmupSummaryDto | null
  onCreated: (domain: SendingDomainViewDto) => void
  onClose: () => void
}

/**
 * Adding a sending domain: a subdomain of the company website first (it passes the website check and starts with
 * the website's trust), a separate domain second, with the trade-off of each. Never the website's own domain: the
 * API refuses it, and the refusal shows under the field.
 */
export function AddDomainForm({ clientCompanyId, companyDomain, summary, onCreated, onClose }: AddDomainFormProps) {
  const { showSuccess } = useToast()
  const [kind, setKind] = useState<NewDomainKind>('subdomain')
  const [subdomain, setSubdomain] = useState('events')
  const [separateDomain, setSeparateDomain] = useState('')
  const [region, setRegion] = useState(DEFAULT_SENDING_DOMAIN_REGION)
  const [creating, setCreating] = useState(false)
  const [domainErrors, setDomainErrors] = useState<string[]>([])
  const [regionErrors, setRegionErrors] = useState<string[]>([])

  // Without a website there is nothing to be a subdomain of, so the separate domain is the only choice.
  const chosen: NewDomainKind = companyDomain ? kind : 'separate'
  const domain = chosen === 'subdomain'
    ? (subdomain.trim() ? `${subdomain.trim()}.${companyDomain}` : '')
    : separateDomain.trim()
  const tradeOff = companyDomain ? subdomainTradeOff(companyDomain) : null

  const choose = (next: NewDomainKind) => {
    setKind(next)
    setDomainErrors([])
  }

  const create = async () => {
    if (!domain || creating) return
    setCreating(true)
    setDomainErrors([])
    setRegionErrors([])
    const res = await sendingDomainsApi.create(clientCompanyId, { Domain: domain, Region: region })
    setCreating(false)
    if (res.error || !res.data) {
      const refusals = parseValidationErrors({ error: res.error })
      const forRegion = refusals.filter(refusal => refusal.FieldName === 'Region').map(refusal => refusal.Message)
      const forDomain = refusals.filter(refusal => refusal.FieldName !== 'Region').map(refusal => refusal.Message)
      setRegionErrors(forRegion)
      setDomainErrors(forDomain.length > 0 || forRegion.length > 0
        ? forDomain
        : getDisplayErrorMessages(res.error, 'The domain could not be added.'))
      return
    }
    showSuccess('Domain added', 'Add the DNS records below, then press Check again.')
    onCreated(res.data)
  }

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <button type="button" onClick={onClose} disabled={creating} className={GHOST_BUTTON}>Cancel</button>
      <button type="button" onClick={() => void create()} disabled={creating || !domain} className={PRIMARY_BUTTON}>
        {creating && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Add domain
      </button>
    </div>
  )

  const domainError = domainErrors.join(' ') || undefined
  const companySettings = <a href={routes.settingsCompany(clientCompanyId)} className={TEXT_LINK}>Company settings</a>

  return (
    <DomainOverlay
      title="Add sending domain"
      subtitle="Choose the domain your emails come from."
      onEscape={() => { if (!creating) onClose() }}
      onClose={onClose}
      footer={footer}
    >
      <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
        <div className="space-y-8">
          <fieldset>
            <legend className="mb-2.5 text-sm font-medium text-slate-700 dark:text-slate-200">Domain</legend>
            <div className="space-y-3">
              <Choice
                kind="subdomain"
                selected={chosen === 'subdomain'}
                disabled={!companyDomain}
                onSelect={choose}
                title={companyDomain
                  ? <>A subdomain of {companyDomain} <span className="font-normal text-slate-600 dark:text-slate-400">(recommended)</span></>
                  : <>A subdomain of your company's website</>}
                description={companyDomain && tradeOff ? (
                  <>
                    <p className="text-[13px] text-slate-600 dark:text-slate-400">{subdomainChoiceSentence(companyDomain)}</p>
                    <p className="mt-2 text-[13px] text-slate-600 dark:text-slate-400">
                      <span className="font-medium text-slate-800 dark:text-slate-200">{tradeOff.lead}</span> {tradeOff.text}
                    </p>
                  </>
                ) : (
                  <p className="text-[13px] text-slate-600 dark:text-slate-400">
                    Set your company's Website in {companySettings} to send from a subdomain of it, which passes the website
                    check automatically.
                  </p>
                )}
              >
                {companyDomain && chosen === 'subdomain' && (
                  <SubdomainField
                    value={subdomain}
                    onChange={setSubdomain}
                    companyDomain={companyDomain}
                    error={domainError}
                    disabled={creating}
                    help={<>{companyDomain} comes from your company's Website in {companySettings}.</>}
                  />
                )}
              </Choice>

              <Choice
                kind="separate"
                selected={chosen === 'separate'}
                onSelect={choose}
                title="A separate domain"
                description={<p className="text-[13px] text-slate-600 dark:text-slate-400">{separateDomainSentence(companyDomain)}</p>}
              >
                {chosen === 'separate' && (
                  <div className="mt-4">
                    <TextBox
                      name="separate-domain"
                      label="Domain"
                      value={separateDomain}
                      onChange={setSeparateDomain}
                      placeholder={companyDomain ? `e-${companyDomain}` : 'e-yourbrand.com'}
                      autoComplete="off"
                      error={domainError}
                      disabled={creating}
                      containerClassName="max-w-sm"
                    />
                  </div>
                )}
              </Choice>
            </div>
          </fieldset>

          <Select
            name="region"
            label="Data residency region"
            options={SENDING_DOMAIN_REGIONS}
            value={region}
            onChange={(event: Event) => setRegion((event.target as HTMLSelectElement).value)}
            error={regionErrors.join(' ') || undefined}
            helperText={REGION_HELP}
            disabled={creating}
          />
        </div>

        {summary && (
          <WhatHappensNext
            chosen={chosen}
            domain={domain}
            companyDomain={companyDomain}
            summary={summary}
          />
        )}
      </div>
    </DomainOverlay>
  )
}

function Choice({ kind, selected, disabled = false, onSelect, title, description, children }: {
  kind: NewDomainKind
  selected: boolean
  disabled?: boolean
  onSelect: (kind: NewDomainKind) => void
  title: ComponentChildren
  /** Read out with the radio; the choice's own field (children) sits after it, outside the description. */
  description: ComponentChildren
  children?: ComponentChildren
}) {
  const id = useId()
  const descriptionId = useId()
  return (
    <div className={`rounded-lg border p-5 ${selected
      ? 'border-primary-600 ring-1 ring-primary-600 dark:border-primary-500 dark:ring-primary-500'
      : 'border-slate-200 dark:border-slate-700'} ${disabled ? 'opacity-75' : ''}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          id={id}
          name="domain-kind"
          value={kind}
          checked={selected}
          disabled={disabled}
          onChange={() => onSelect(kind)}
          aria-describedby={descriptionId}
          className="mt-0.5 h-4 w-4 accent-primary-700"
        />
        <div className="min-w-0 grow">
          <label htmlFor={id} className="text-sm font-medium text-slate-900 dark:text-white">{title}</label>
          <div id={descriptionId} className="mt-1">{description}</div>
          {children}
        </div>
      </div>
    </div>
  )
}

/** The subdomain's own label, with the company website fixed after it. */
function SubdomainField({ value, onChange, companyDomain, error, disabled, help }: {
  value: string
  onChange: (value: string) => void
  companyDomain: string
  error?: string
  disabled: boolean
  help: ComponentChildren
}) {
  const errorId = useId()
  const helpId = useId()
  return (
    <div className="mt-4 space-y-1.5">
      <label htmlFor="subdomain" className="block text-xs font-medium text-slate-700 dark:text-slate-200">Subdomain</label>
      <div className={`flex max-w-sm items-stretch overflow-hidden rounded-lg border focus-within:ring-2 focus-within:ring-primary-500 ${error
        ? 'border-red-300'
        : 'border-slate-300 dark:border-slate-600'}`}
      >
        <input
          id="subdomain"
          type="text"
          value={value}
          onInput={event => onChange((event.target as HTMLInputElement).value)}
          autoComplete="off"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : helpId}
          className="min-w-0 flex-1 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none disabled:opacity-60 dark:bg-slate-800 dark:text-white"
        />
        <span className="flex flex-none items-center border-l border-slate-300 bg-slate-100 px-3 text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
          .{companyDomain}
        </span>
      </div>
      {error && <p id={errorId} className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <p id={helpId} className="text-xs text-slate-600 dark:text-slate-400">{help}</p>
    </div>
  )
}

/** Setting up, Warming and Ready, explained before anything is added, with the API's own numbers. */
function WhatHappensNext({ chosen, domain, companyDomain, summary }: {
  chosen: NewDomainKind
  domain: string
  companyDomain: string | null
  summary: SendingDomainWarmupSummaryDto
}) {
  const headingId = useId()
  const text = 'text-[13px] text-slate-600 dark:text-slate-400'
  const strong = 'font-medium text-slate-800 dark:text-slate-200'

  return (
    <aside aria-labelledby={headingId} className="space-y-6 rounded-lg border border-slate-200 bg-slate-50 px-5 pb-6 pt-5 dark:border-slate-700 dark:bg-slate-800/40">
      <h3 id={headingId} className="text-sm font-semibold text-slate-900 dark:text-white">What happens next</h3>

      <section className="space-y-2">
        <DomainStatePill domain={{ State: 'SettingUp' }} />
        <p className={text}>{SETTING_UP_NEXT}</p>
        <ul className="space-y-2">
          {settingUpNextLines(chosen, domain, companyDomain, COMPANY_WEBSITE_PLACE.settings).map(line => (
            <li key={line.lead} className={text}><span className={strong}>{line.lead}</span> {line.text}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-2">
        <DomainStatePill domain={{ State: 'Warming' }} />
        {warmingNextParagraphs(summary).map((runs, index) => (
          <p key={index} className={text}><CopyRuns runs={runs} strongClassName={strong} /></p>
        ))}
      </section>

      <section className="space-y-2">
        <DomainStatePill domain={{ State: 'Ready' }} />
        <p className={text}>{READY_NEXT}</p>
      </section>
    </aside>
  )
}
